import * as React from 'react'
import { cn } from '../../lib/cn'

export interface PipelineConnectorProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stroke color (defaults to subtle border token). */
  color?: string
  /** Stroke opacity (0..1). */
  opacity?: number
  /** Gap width in pixels. Mockup default: 32. */
  width?: number
}

/**
 * Horizontal connector line between stage columns. SVG renders a
 * 1.5px stroke across the full height of the canvas row. Width is
 * fixed; vertical stretch is handled by parent flex `align-items:
 * stretch`.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.connector` (non-fork).
 */
export const PipelineConnector = React.forwardRef<HTMLDivElement, PipelineConnectorProps>(
  function PipelineConnector(
    { className, color = 'var(--pe-border-card)', opacity = 1, width = 32, style, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn('relative h-full shrink-0', className)}
        style={{ width, minWidth: width, ...style }}
        data-testid="pipeline-connector"
        {...rest}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
        >
          <line
            x1={0}
            y1={50}
            x2={100}
            y2={50}
            stroke={color}
            strokeWidth={1.5}
            // The SVG stretches to the full canvas-row height via
            // preserveAspectRatio="none"; without a non-scaling stroke the
            // 1.5px line is squashed to a hairline at tall canvas heights.
            // Pinning the stroke keeps the connector crisp + visible.
            vectorEffect="non-scaling-stroke"
            opacity={opacity}
          />
        </svg>
      </div>
    )
  },
)
